import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../context/LanguageContext';
import { query, where, onSnapshot } from 'firebase/firestore';
import { getClientCollection } from '../../../../../services/FirebaseService';
import '../style/transparent-pricing-section.css';
import { initializePricingMobileSlider } from '../script/transparent-pricing-section';
import { initializeTitleAnimation } from '../script/title-animation';
import servicePack from '../../../../../assets/logo/services/service_pack.webp';
import pricing2 from '../../../../../assets/logo/pricing/2.webp';
import pricing3 from '../../../../../assets/logo/pricing/3.webp';

const TransparentPricingSection: React.FC = () => {
    const { t } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);

    const translation = t as any;
    const pricingTranslations = translation.PRICING || t.PRICING;
    const { language } = useLanguage();

    const [pricingAmounts, setPricingAmounts] = useState({
        customReview: '29.90',
        oilReview: '39.90',
        oilFilterReview: '59.90'
    });

    const [pricingOptions, setPricingOptions] = useState({
        // Custom Review Options
        customWithoutFilter: '29.90',
        customWithFilter: '44.90',
        // Oil Review Options
        oil10W40: '39.90',
        oilCEPSA10W40: '49.90',
        oilCEPSA5W40: '59.90',
        oilCEPSA5W30: '74.89',
        oilCASTROL5W30: '84.89',
        // Oil + Filter Review Options
        oilFilter10W40: '59.90',
        oilFilterCEPSA10W40: '69.90',
        oilFilterCEPSA5W40: '79.90',
        oilFilterCEPSA5W30: '89.90'
    });

    // Store latest translations and pricing options in ref to access in modal function
    const pricingTranslationsRef = useRef(pricingTranslations);
    const currentLanguageRef = useRef(language);
    const pricingOptionsRef = useRef(pricingOptions);

    useEffect(() => {
        pricingTranslationsRef.current = pricingTranslations;
        currentLanguageRef.current = language;
    }, [pricingTranslations, language]);

    useEffect(() => {
        pricingOptionsRef.current = pricingOptions;
    }, [pricingOptions]);

    // Load pricing amounts from Firestore
    useEffect(() => {
        const pricingsQuery = query(
            getClientCollection('pricings'),
            where('type', '==', 'transparent')
        );

        const unsubscribe = onSnapshot(pricingsQuery, (snapshot) => {
            if (snapshot.docs.length > 0) {
                const data = snapshot.docs[0].data();
                setPricingAmounts({
                    customReview: data.customReview || '29.90',
                    oilReview: data.oilReview || '39.90',
                    oilFilterReview: data.oilFilterReview || '59.90'
                });
                
                // Load pricing options if they exist
                if (data.options) {
                    setPricingOptions({
                        customWithoutFilter: data.options.customWithoutFilter || '29.90',
                        customWithFilter: data.options.customWithFilter || '44.90',
                        oil10W40: data.options.oil10W40 || '39.90',
                        oilCEPSA10W40: data.options.oilCEPSA10W40 || '49.90',
                        oilCEPSA5W40: data.options.oilCEPSA5W40 || '59.90',
                        oilCEPSA5W30: data.options.oilCEPSA5W30 || '74.89',
                        oilCASTROL5W30: data.options.oilCASTROL5W30 || '84.89',
                        oilFilter10W40: data.options.oilFilter10W40 || '59.90',
                        oilFilterCEPSA10W40: data.options.oilFilterCEPSA10W40 || '69.90',
                        oilFilterCEPSA5W40: data.options.oilFilterCEPSA5W40 || '79.90',
                        oilFilterCEPSA5W30: data.options.oilFilterCEPSA5W30 || '89.90'
                    });
                }
            }
        }, () => {
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        // Initialize mobile slider if on mobile
        if (window.innerWidth <= 991) {
            setTimeout(() => {
                initializePricingMobileSlider(containerRef.current);
            }, 500);
        }

        const hasInitialized = sessionStorage.getItem('pricing-titles-initialized');
        
        // Initialize title animation
        const timer = setTimeout(() => {
            if (!hasInitialized) {
            initializeTitleAnimation();
                sessionStorage.setItem('pricing-titles-initialized', 'true');
            } else {
                // On language change, just make titles visible without animation
                const titles = document.querySelectorAll('.transparent-pricing-one .title-animation');
                titles.forEach((title: Element) => {
                    const el = title as HTMLElement;
                    el.style.opacity = '1';
                });
            }
            
            // Refresh ScrollTrigger
            if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                (window as any).ScrollTrigger.refresh();
            }
        }, 300);

        // Re-initialize on window resize (only if crossing mobile threshold)
        let wasMobile = window.innerWidth <= 991;
        const resizeHandler = () => {
            const isMobile = window.innerWidth <= 991;
            if (isMobile !== wasMobile) {
                wasMobile = isMobile;
                if (isMobile) {
                    setTimeout(() => {
                        initializePricingMobileSlider(containerRef.current);
                    }, 100);
                }
            }
        };

        window.addEventListener('resize', resizeHandler);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', resizeHandler);
        };
    }, [language]); // Re-run when language changes

    // Define modal functions using refs to always get latest translations
    const openPricingModal = React.useCallback((type: string) => {
        const modal = document.getElementById('pricingModal');
        const title = document.getElementById('modalTitle');
        const options = document.getElementById('pricingOptions');

        if (!modal || !title || !options) return;

        // Get current translations and pricing options from refs (always up-to-date)
        const currentTranslations = pricingTranslationsRef.current;
        const currentPricingOptions = pricingOptionsRef.current;
        const lang = currentLanguageRef.current || 'pt';

        let pricingData: { [key: string]: string } = {};
        let titleText = '';

        // Helper to get translation value
        const getTranslation = (key: string, fallback: string = '') => {
            if (!currentTranslations || !currentTranslations[key]) return fallback;
            const translation = currentTranslations[key];
            if (typeof translation === 'object' && (translation as any)[lang]) {
                return (translation as any)[lang];
            }
            if (typeof translation === 'string') {
                return translation;
            }
            return fallback;
        };

        switch (type) {
            case 'custom':
                titleText = getTranslation('customReviewOptionsTitle', 'Revisão Personalizada - Opções');
                pricingData = {
                    [getTranslation('optionWithoutFilter', 'Sem filtro')]: `${currentPricingOptions.customWithoutFilter}€`,
                    [getTranslation('optionWithFilter', 'Com Filtro')]: `${currentPricingOptions.customWithFilter}€`
                };
                break;
            case 'oil':
                titleText = getTranslation('oilReviewOptionsTitle', 'Revisão com Óleo - Opções');
                pricingData = {
                    [getTranslation('option10W40', '10W40')]: `${currentPricingOptions.oil10W40}€`,
                    [getTranslation('optionOilCEPSA10W40', 'Óleo CEPSA 10W40')]: `${currentPricingOptions.oilCEPSA10W40}€`,
                    [getTranslation('optionOilCEPSA5W40', 'Óleo CEPSA 5W40')]: `${currentPricingOptions.oilCEPSA5W40}€`,
                    [getTranslation('optionOilCEPSA5W30', 'Óleo CEPSA 5W30')]: `${currentPricingOptions.oilCEPSA5W30}€`,
                    [getTranslation('optionOilCASTROL5W30', 'Óleo CASTROL 5W30')]: `${currentPricingOptions.oilCASTROL5W30}€`
                };
                break;
            case 'oilFilter':
                titleText = getTranslation('oilFilterReviewOptionsTitle', 'Revisão com Óleo + Filtro - Opções');
                pricingData = {
                    [getTranslation('optionOil10W40', 'Óleo 10W40')]: `${currentPricingOptions.oilFilter10W40}€`,
                    [getTranslation('optionOilCEPSA10W40', 'Óleo CEPSA 10W40')]: `${currentPricingOptions.oilFilterCEPSA10W40}€`,
                    [getTranslation('optionOilCEPSA5W40', 'Óleo CEPSA 5W40')]: `${currentPricingOptions.oilFilterCEPSA5W40}€`,
                    [getTranslation('optionOilCEPSA5W30', 'Óleo CEPSA 5W30')]: `${currentPricingOptions.oilFilterCEPSA5W30}€`
                };
                break;
        }

        title.textContent = titleText;
        options.innerHTML = '';

        Object.entries(pricingData).forEach(([label, price]) => {
            const optionDiv = document.createElement('div');
            optionDiv.className = 'pricing-option';
            optionDiv.innerHTML = `<strong>${label}</strong> - ${price}`;
            options.appendChild(optionDiv);
        });

        modal.style.display = 'block';
    }, []);

    const closePricingModal = React.useCallback(() => {
        const modal = document.getElementById('pricingModal');
        if (modal) {
            modal.style.display = 'none';
        }
    }, []);

    // Update window functions whenever translations change
    useEffect(() => {
        if (typeof window !== 'undefined') {
            (window as any).openPricingModal = openPricingModal;
            (window as any).closePricingModal = closePricingModal;
        }
    }, [openPricingModal, closePricingModal]);

    // Update tooltips when language changes
    useEffect(() => {
        updateTooltips();
    }, [pricingTranslations]);

    const updateTooltips = () => {
        const filterTooltips = document.querySelectorAll('[data-tooltip="filterTooltip"]');
        const controlPointsTooltips = document.querySelectorAll('[data-tooltip="controlPointsTooltip"]');

        filterTooltips.forEach((icon) => {
            icon.setAttribute('data-tooltip-text', pricingTranslations?.filterTooltip || '');
        });

        controlPointsTooltips.forEach((icon) => {
            icon.setAttribute('data-tooltip-text', pricingTranslations?.controlPointsTooltip || '');
        });
    };


    return (
        <>
            <section className="pricing-transparent" ref={containerRef}>
                <div className="container">
                    <div className="section-title text-center sec-title-animation animation-style2">
                        <div className="section-title__tagline-box">
                            <div className="section-title__tagline-border">
                                <div className="section-title__shape-1">
                                    <i className="section-title__circle"></i>
                                </div>
                            </div>
                            <h6 className="section-title__tagline">
                                {pricingTranslations?.transparentPricing || 'Preços Transparentes'}
                            </h6>
                            <div className="section-title__tagline-border">
                                <div className="section-title__shape-2">
                                    <i className="section-title__circle"></i>
                                </div>
                            </div>
                        </div>
                        <h2
                            className="section-title__title title-animation"
                            key={`pricing-title-${language}`}
                        >
                            {pricingTranslations?.howWeWork || 'Como trabalhamos'}
                        </h2>
                        <p className="pricing-transparent__intro-text">
                            {pricingTranslations?.pricingIntroText || 'Revisões adaptadas a cada veículo, realizadas por técnicos certificados com controlo total de qualidade.'}
                        </p>
                    </div>
                    <div className="row justify-content-center">
                        {/* Card 1: Custom Review */}
                        <div className="col-xl-4 col-lg-6 col-md-6 wow fadeInLeft" data-wow-delay="100ms">
                            <div className="pricing-transparent__single">
                                <div className="pricing-transparent__single-inner">
                                    <div className="pricing-transparent__img">
                                        <img loading="lazy" 
                                            src={servicePack} 
                                            alt="Revisão Personalizada" 
                                            style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                        />
                                    </div>
                                    <div className="pricing-transparent__content">
                                        <h4 className="pricing-transparent__title" data-translate="customReviewTitle">
                                            {pricingTranslations?.customReviewTitle || 'Revisão personalizada'}
                                        </h4>
                                        <p className="pricing-transparent__text" data-translate="customReviewDescription">
                                            <strong>
                                                {pricingTranslations?.customReviewTitleBold || 'REVISÃO PERSONALIZADA'}
                                                <span className="info-icon" data-tooltip="filterTooltip" data-tooltip-text={pricingTranslations?.filterTooltip || ''}>
                                                    <i className="fas fa-info-circle"></i>
                                                </span>
                                            </strong>
                                            <br /><br />
                                            • {pricingTranslations?.motorOilPurchasedSeparately || 'Óleo de Motor adquirido separadamente'}<br />
                                            • {pricingTranslations?.laborVerification || 'Mão de obra + Verificação'}<br />
                                            • {pricingTranslations?.controlPoints || 'Pontos de Controlo'}
                                            <span className="info-icon" data-tooltip="controlPointsTooltip" data-tooltip-text={pricingTranslations?.controlPointsTooltip || ''}>
                                                <i className="fas fa-info-circle"></i>
                                            </span><br />
                                            • {pricingTranslations?.windowCleanerReplacement || 'Limpa Vidros (Reposição)'}<br /><br />
                                            <button className="view-options-btn" onClick={() => openPricingModal('custom')}>
                                                {pricingTranslations?.viewOptions || 'Ver Opções'}
                                            </button>
                                        </p>
                                        <div className="pricing-transparent__price">
                                            <span className="pricing-transparent__currency">
                                                {pricingTranslations?.from || 'Desde'}
                                            </span>
                                            <span className="pricing-transparent__amount">{pricingAmounts.customReview}€</span>
                                        </div>
                                        <div className="pricing-transparent__btn-box">
                                            <Link to="/orcamento" className="thm-btn pricing-budget-btn">
                                                {pricingTranslations?.scheduleService || 'Peça orçamento'}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card 2: Oil Review */}
                        <div className="col-xl-4 col-lg-6 col-md-6 wow fadeInUp" data-wow-delay="200ms">
                            <div className="pricing-transparent__single">
                                <div className="pricing-transparent__single-inner">
                                    <div className="pricing-transparent__img">
                                        <img loading="lazy" 
                                            src={pricing2} 
                                            alt="Revisão com Óleo" 
                                            style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                        />
                                    </div>
                                    <div className="pricing-transparent__content">
                                        <h4 className="pricing-transparent__title" data-translate="oilReviewIncludedTitle">
                                            {pricingTranslations?.oilReviewIncludedTitle || 'Revisão com óleo incluído'}
                                        </h4>
                                        <p className="pricing-transparent__text" data-translate="oilReviewIncludedDescription">
                                            <strong>{pricingTranslations?.oilReviewTitleBold || 'REVISÃO COM ÓLEO'}</strong>
                                            <br /><br />
                                            • {pricingTranslations?.laborVerification || 'Mão de obra + Verificação'}<br />
                                            • {pricingTranslations?.controlPoints || 'Pontos de Controlo'}
                                            <span className="info-icon" data-tooltip="controlPointsTooltip" data-tooltip-text={pricingTranslations?.controlPointsTooltip || ''}>
                                                <i className="fas fa-info-circle"></i>
                                            </span><br />
                                            • {pricingTranslations?.windowCleanerReplacement || 'Limpa Vidros (Reposição)'}<br />
                                            • {pricingTranslations?.oilOnly || 'Óleo'}<br /><br />
                                            <button className="view-options-btn" onClick={() => openPricingModal('oil')}>
                                                {pricingTranslations?.viewOptions || 'Ver Opções'}
                                            </button>
                                        </p>
                                        <div className="pricing-transparent__price">
                                            <span className="pricing-transparent__currency">
                                                {pricingTranslations?.from || 'Desde'}
                                            </span>
                                            <span className="pricing-transparent__amount">{pricingAmounts.oilReview}€</span>
                                        </div>
                                        <div className="pricing-transparent__btn-box">
                                            <Link to="/orcamento" className="thm-btn pricing-budget-btn">
                                                {pricingTranslations?.scheduleService || 'Peça orçamento'}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card 3: Oil + Filter Review */}
                        <div className="col-xl-4 col-lg-6 col-md-6 wow fadeInRight" data-wow-delay="300ms">
                            <div className="pricing-transparent__single">
                                <div className="pricing-transparent__single-inner">
                                    <div className="pricing-transparent__img">
                                        <img loading="lazy" 
                                            src={pricing3} 
                                            alt="Revisão com Óleo + Filtro" 
                                            style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                        />
                                    </div>
                                    <div className="pricing-transparent__content">
                                        <h4 className="pricing-transparent__title" data-translate="oilFilterIncludedTitle">
                                            {pricingTranslations?.oilFilterIncludedTitle || 'Revisão com filtro de óleo incluído'}
                                        </h4>
                                        <p className="pricing-transparent__text" data-translate="oilFilterIncludedDescription">
                                            <strong>
                                                {pricingTranslations?.oilFilterReviewTitleBold || 'REVISÃO COM ÓLEO + FILTRO'}
                                                <span className="info-icon" data-tooltip="filterTooltip" data-tooltip-text={pricingTranslations?.filterTooltip || ''}>
                                                    <i className="fas fa-info-circle"></i>
                                                </span>
                                            </strong>
                                            <br /><br />
                                            • {pricingTranslations?.laborVerification || 'Mão de obra + Verificação'}<br />
                                            • {pricingTranslations?.controlPoints || 'Pontos de Controlo'}
                                            <span className="info-icon" data-tooltip="controlPointsTooltip" data-tooltip-text={pricingTranslations?.controlPointsTooltip || ''}>
                                                <i className="fas fa-info-circle"></i>
                                            </span><br />
                                            • {pricingTranslations?.windowCleanerReplacement || 'Limpa Vidros (Reposição)'}<br />
                                            • {pricingTranslations?.oilFilterIncludedText || 'Óleo + Filtro incluídos'}<br /><br />
                                            <button className="view-options-btn" onClick={() => openPricingModal('oilFilter')}>
                                                {pricingTranslations?.viewOptions || 'Ver Opções'}
                                            </button>
                                        </p>
                                        <div className="pricing-transparent__price">
                                            <span className="pricing-transparent__currency">
                                                {pricingTranslations?.from || 'Desde'}
                                            </span>
                                            <span className="pricing-transparent__amount">{pricingAmounts.oilFilterReview}€</span>
                                        </div>
                                        <div className="pricing-transparent__btn-box">
                                            <Link to="/orcamento" className="thm-btn pricing-budget-btn">
                                                {pricingTranslations?.scheduleService || 'Peça orçamento'}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Modal */}
            <div id="pricingModal" className="pricing-modal" onClick={closePricingModal}>
                <div className="pricing-modal-content" onClick={(e) => e.stopPropagation()}>
                    <span className="pricing-modal-close" onClick={closePricingModal}>&times;</span>
                    <h3 id="modalTitle">Opções de Preço</h3>
                    <div id="pricingOptions" className="pricing-options"></div>
                </div>
            </div>
        </>
    );
};

export default TransparentPricingSection;

